class CartDrawerV2 extends HTMLElement {
  constructor() {
    super();

    this._stylesReady = false;
    this._stylesReadyPromise = this.waitForStyles();
    this._readyReleaseToken = 0;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawerV2-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this._stylesReadyPromise.finally(() => {
      this._stylesReady = true;
    });
  }

  setReadyState(isReady) {
    const drawerRoot = this.querySelector('#CartDrawerV2');

    drawerRoot?.classList.toggle('cart-drawer-v2--booting', !isReady);
    drawerRoot?.setAttribute('data-ready', isReady ? 'true' : 'false');
  }

  syncEmptyState(cartState = null) {
    const drawerInner = this.querySelector('#CartDrawerV2 .cart-drawer-v2__inner');
    const liveCart = cartState || window.liquidAjaxCart?.getState?.()?.cart || window.liquidAjaxCart?.cart || null;

    if (!drawerInner || typeof liveCart?.item_count !== 'number') {
      return;
    }

    drawerInner.classList.toggle('cart-drawer-v2__inner--empty', liveCart.item_count === 0);
  }

  waitForNextPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  }

  async releaseReadyState() {
    const token = ++this._readyReleaseToken;

    this.setReadyState(false);
    await this._stylesReadyPromise;
    this.syncEmptyState();
    await this.waitForNextPaint();

    if (token !== this._readyReleaseToken) {
      return;
    }

    this.setReadyState(true);
  }

  waitForStyles() {
    const requiredStyles = [
      'cart-drawer-v2.css',
      'component-cart-items.css',
      'quantity-popover.css',
      'component-card.css',
      'cart-upsell.css',
      'checkout-button.css',
      'modal.css',
    ];

    const links = requiredStyles
      .map((fileName) => document.querySelector(`link[rel="stylesheet"][href*="${fileName}"]`))
      .filter(Boolean);

    if (!links.length) {
      return Promise.resolve();
    }

    const isLoaded = (link) => {
      try {
        return Array.from(document.styleSheets).some((sheet) => sheet.href && sheet.href.includes(link.getAttribute('href')));
      } catch (error) {
        return Boolean(link.sheet);
      }
    };

    return Promise.all(links.map((link) => {
      if (isLoaded(link)) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const handleLoad = () => {
          link.removeEventListener('load', handleLoad);
          link.removeEventListener('error', handleLoad);
          resolve();
        };

        link.addEventListener('load', handleLoad, { once: true });
        link.addEventListener('error', handleLoad, { once: true });
      });
    }));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    document.addEventListener('liquid-ajax-cart:init', () => {
      this.syncEmptyState(window.liquidAjaxCart?.getState?.()?.cart || window.liquidAjaxCart?.cart || null);
    });

    this.syncEmptyState();

    document.addEventListener('cart:updated', (event) => {
      this.syncEmptyState(event.detail?.cart || null);
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.releaseReadyState();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.querySelector('.cart-drawer-v2__empty') || document.getElementById('CartDrawerV2');
        const focusElement = this.querySelector('.cart-drawer-v2__inner') || this.querySelector('.cart-drawer-v2__heading__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this._readyReleaseToken += 1;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.setReadyState(false);

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.syncEmptyState(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawerV2-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawerV2',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer-v2', CartDrawerV2);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawerV2',
        section: 'cart-drawer',
        selector: '#CartDrawerV2',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}
